import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FontText } from './FontText';

interface FAQItem {
  q: string;
  a: string;
}

interface FAQSectionProps {
  faqs: FAQItem[];
}

export const FAQSection: React.FC<FAQSectionProps> = ({ faqs }) => {
  const { theme } = useTheme();
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const toggleItem = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const styles: Record<string, React.CSSProperties> = {
    container: {
      marginTop: 60,
      paddingTop: 40,
      borderTop: `1px solid ${theme.colors.overlay}`,
    },
    title: {
      color: theme.colors.text,
      marginBottom: 30,
      textAlign: 'center',
    },
    faqItem: {
      backgroundColor: theme.colors.surface,
      borderRadius: 12,
      marginBottom: 16,
      border: `1px solid ${theme.colors.overlay}`,
      overflow: 'hidden',
    },
    question: {
      padding: 20,
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transition: 'background-color 0.2s',
    },
    questionText: {
      color: theme.colors.text,
      flex: 1,
    },
    expandIcon: {
      color: theme.colors.textSecondary,
      fontSize: 18,
      transition: 'transform 0.2s',
      marginLeft: 16,
    },
    expandIconOpen: {
      transform: 'rotate(180deg)',
    },
    answer: {
      padding: '0 20px 20px 20px',
      borderTop: `1px solid ${theme.colors.overlay}`,
    },
    answerText: {
      color: theme.colors.textSecondary,
      lineHeight: '1.6',
    },
  };

  return (
    <div style={styles.container}>
      <FontText h2 style={styles.title}>
        Frequently Asked Questions
      </FontText>
      <div>
        {faqs.map((faq, index) => {
          const isExpanded = expandedItems.has(index);
          return (
            <div key={index} style={styles.faqItem}>
              <div
                style={styles.question}
                onClick={() => toggleItem(index)}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = theme.colors.overlay;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <FontText normal style={styles.questionText}>
                  {faq.q}
                </FontText>
                <span
                  style={{
                    ...styles.expandIcon,
                    ...(isExpanded ? styles.expandIconOpen : {}),
                  }}
                >
                  ▼
                </span>
              </div>
              {isExpanded && (
                <div style={styles.answer}>
                  <FontText normal style={styles.answerText}>
                    {faq.a}
                  </FontText>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
